<template>
  <div
    class="pass-history-view appointment-pass-view datatable-container container mb-5"
  >
    <Loader
      :is-processing="state.isLoading || areAppointmentPassesLoading"
      loader-text="Loading"
    />
    <div class="row mt-0 mt-sm-4">
      <CCol md="12">
        <DashboardActionPanel
          class="mb-4"
          :show-search="true"
          :show-sort="!isAppoinmentMode ? true : false"
          :show-export="true"
          :show-top-left-slot="true"
          @on-export="exportAsCSV()"
          @on-search="searchByQuery"
          @on-sort="toggleFilters"
        >
          <template #right-content>
            <div
              v-show="isActiveDashboardUpdate"
              class="py-2 px-2 cursor-pointer"
              @click="
                $store.commit('dashboardTable/SET_DASHBOARD_UPDATES', false)
              "
            >
              <i class="ri-pause-circle-fill color-base-red me-2"></i>Pause
              Dashboard
            </div>
            <div
              v-show="!isActiveDashboardUpdate"
              class="py-2 px-2 cursor-pointer"
              @click="resumeDashboardUpdate"
            >
              <i class="ri-play-line me-2"></i>Resume Dashboard
            </div>
          </template>
          <template #right-last-content>
            <Dropdown
              v-if="!isAppoinmentMode"
              auto-close="outside"
              @update:model-value="onColumnOptionsChange"
              :preselected="isStudentSisIdEnabled"
              :items="tableDropdowns.column"
              :check-list-mode="true"
              class="left-box-separator"
            >
              <template #toggler>
                <div class="d-flex align-items-center">
                  <IconButton class="static" icon="ri-table-line"></IconButton>
                  <span>Columns</span>
                </div>
              </template>
            </Dropdown>
            <Dropdown
              v-if="isAppoinmentMode"
              auto-close="outside"
              @update:model-value="onColumnOptionsChange"
              :preselected="isAptStudentSisIdEnabled"
              :items="tableDropdowns.columnApt"
              :check-list-mode="true"
              class="left-box-separator"
            >
              <template #toggler>
                <div class="d-flex align-items-center">
                  <IconButton class="static" icon="ri-table-line"></IconButton>
                  <span>Columns</span>
                </div>
              </template>
            </Dropdown>
            <Dropdown
              v-if="!isAppoinmentMode"
              v-model="state.currentView"
              reduce
              :items="tableDropdowns.tableView"
              :preselected="tableDropdowns.tableView[0]"
              class="ms-2 me-2 left-box-separator"
              @update:model-value="setActiveView(state.currentView)"
            >
              <template #toggler>
                <div class="d-flex align-items-center">
                  <IconButton
                    class="static"
                    icon="ri-layout-row-line"
                  ></IconButton>
                  <span>View</span>
                </div>
              </template>
            </Dropdown>
          </template>
          <template #bottom-content>
            <div v-if="state.isFilterVisible && !isAppoinmentMode">
              <DashboardTimeFilters class="mt-3"> </DashboardTimeFilters>
              <hr />
              <CRow class="justify-content-center mt-3">
                <CCol md="4">
                  <LazyLoadSelect
                    :is-multiple="true"
                    class="flex-1 me-2 mt-1"
                    :selected="filterData.by_students"
                    type="student"
                    placeholder="Search students"
                    @changed="
                      (value) => {
                        $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                          filter: 'by_students',
                          value: value
                        })
                      }
                    "
                  />
                </CCol>
                <CCol md="4">
                  <LazyLoadSelect
                    :is-multiple="true"
                    class="flex-1 me-2 mt-1"
                    :selected="filterData.by_teachers"
                    type="adults"
                    placeholder="Search teachers"
                    @changed="
                      (value) => {
                        $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                          filter: 'by_teachers',
                          value: value
                        })
                      }
                    "
                  />
                </CCol>
                <CCol md="4">
                  <LazyLoadSelect
                    :is-multiple="true"
                    :is-processing="false"
                    class="flex-1 me-2 mt-1"
                    :selected="filterData.by_rooms"
                    type="locations"
                    placeholder="Search rooms"
                    @changed="
                      (value) => {
                        $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                          filter: 'by_rooms',
                          value: value
                        })
                      }
                    "
                  />
                </CCol>
                <div class="text-end mt-3 mb-2">
                  <BaseButton rounded class="me-1" @click="clearFilter"
                    >Clear All</BaseButton
                  >
                  <BaseButton rounded solid @click="advancedFilter"
                    >Filter</BaseButton
                  >
                </div>
              </CRow>
            </div>
          </template>
        </DashboardActionPanel>

        <DataTableOptimized
          v-if="!state.isLoading && !isAppoinmentMode"
          id="dashboardTable"
          class="datatable"
          :class="{ 'basic-view': state.isBasicView }"
          add-table-classes="app-pass-datatable"
          :items="dashboardPasses"
          :fields="activeDataTableFields"
          :sorter="{ external: true }"
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          :basic-view="state.isBasicView"
          @update:sorter-value="sortByColumn"
          @row-clicked="setActiveRow"
        >
          <template #over-table>
            <HelpCenterButton
              classes="top-right mt-2 me-2"
              width="15"
              heigth="15"
              content-key="dashboard_datatable"
            />
          </template>
          <template #student_sis_id="{ item }">
            <td>
              <div class="pass-time-info">
                {{
                  item.user && item.user.student_sis_id
                    ? item.user.student_sis_id
                    : ""
                }}
              </div>
            </td>
          </template>
          <template #user="{ item, styles }">
            <UserAptPass
              :dashboard="true"
              :pass="item"
              :style="styles"
              :is-basic-view="
                state.isBasicView && state.activeTableRow.item.id !== item.id
              "
            />
          </template>

          <template #badge_flags="{ item, styles }">
            <div class="status-field" :style="styles">
              <div
                v-if="getPassStatus(item)"
                :style="`color:${
                  getPassStatus(item).color ? getPassStatus(item).color : ''
                }`"
              >
                {{ getPassStatus(item).label }}
              </div>
            </div>
          </template>

          <template #approved_at="{ item, styles }">
            <div class="pass-time-info align-top" :style="styles">
              <b>{{ item.from ? item.from.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.approved_at
                      ? $helpers.transformDate(item.approved_at, "h:mm A")
                      : ""
                  }}
                </p>
                <InOutColumn :pass="item" section="approved_at" />
                <PassActionButtons :pass="item" :actions-status="1" />
              </div>
            </div>
          </template>

          <template #completed_at="{ item, styles }">
            <div class="pass-time-info in-time align-top" :style="styles">
              <b>{{ item.to ? item.to.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.completed_at
                      ? $helpers.transformDate(item.completed_at, "h:mm A")
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="completed_at" />

                <PassActionButtons :pass="item" :actions-status="2" />
              </div>
            </div>
          </template>

          <template #approved_by="{ item, styles }">
            <div class="pass-time-info align-top" :style="styles">
              <b>{{
                item.child && item.child.from ? item.child.from.name : ""
              }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.child && item.child.approved_at
                      ? $helpers.transformDate(item.child.approved_at, "h:mm A")
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="child_approved_at" />
                <PassActionButtons :pass="item" :actions-status="3" />
              </div>
            </div>
          </template>

          <template #completed_by="{ item, styles }">
            <div class="pass-time-info in-time align-top" :style="styles">
              <b>{{ item.child && item.child.to ? item.child.to.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.child && item.child.completed_at
                      ? $helpers.transformDate(
                          item.child.completed_at,
                          "h:mm A"
                        )
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="child_completed_at" />

                <PassActionButtons :pass="item" :actions-status="4" />
              </div>
            </div>
          </template>

          <template #total_time="{ item, styles }">
            <div class="pass-time-info in-time align-top" :style="styles">
              <b>{{
                (item.total_time && item.badge_flags.ended) ||
                item.badge_flags.system_completed
                  ? item.total_time
                  : ""
              }}</b>
            </div>
          </template>

          <template #type="{ item }">
            <div class="pass-type-icon-container">
              <PassTypeIcon :pass="item"></PassTypeIcon>
            </div>
          </template>

          <template #comments="{ item, styles }">
            <PassComments
              :pass="item"
              :is-basic-view="
                state.isBasicView && state.activeTableRow.item.id !== item.id
              "
              commentable-type="App\Models\Pass"
              :style="styles"
              style="cursor: pointer"
            />
          </template>

          <template #expired_at="{ item, styles }">
            <div :style="styles">
              {{
                item.expired_at ? $helpers.transformDate(item.expired_at) : ""
              }}
            </div>
          </template>
        </DataTableOptimized>
        <DashboardDataTableAptV6 v-if="isAppoinmentMode" ref="aptPassTable" />
      </CCol>
    </div>
  </div>
</template>

<script>
import { reactive, computed, onMounted } from "vue"
import { isAllEntriesPerPageOption } from "@/constants/tablePaginationConstants.js"
import { useStore } from "vuex"
import PassComments from "@/v3components/shared/PassComponents/PassComments.vue"
import PassActionButtons from "@/v3components/shared/PassComponents/PassActionButtons.vue"
import InOutColumn from "@/v3components/shared/PassComponents/InOutColumn.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import tableConfig from "@/v3components/Datatables/DashboardDataTable/dashboardTableConfigOld.js"
import DashboardDataTableAptV6 from "@/v3components/Datatables/DashboardDataTable/DashboardDataTableAptV6.vue"
import DataTableOptimized from "@/v3components/shared/DataTable/DataTableOptimized.vue"
import UserAptPass from "@/v3components/Datatables/AdultAptPassDataTable/UserAptPass.vue"
import PassTypeIcon from "@/v3components/shared/PassComponents/PassTypeIcon.vue"
import DashboardActionPanel from "./TabsAndFilter/DashboardActionPanel.vue"
import DashboardTimeFilters from "./TabsAndFilter/DashboardTimeFilters.vue"
import Dropdown from "@/v3components/shared/Buttons/Dropdown.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import helpers from "@/helpers/index.js"
import download from "@/helpers/downloadCSV"

export default {
  name: "DashboardDataTable",
  components: {
    Loader,
    PassComments,
    PassActionButtons,
    InOutColumn,
    LazyLoadSelect,
    HelpCenterButton,
    DashboardDataTableAptV6,
    DataTableOptimized,
    UserAptPass,
    PassTypeIcon,
    DashboardActionPanel,
    DashboardTimeFilters,
    Dropdown,
    BaseButton,
    IconButton
  },
  setup() {
    const store = useStore()
    const state = reactive({
      isBasicView: false,
      currentView: null,
      isFilterVisible: false,
      isLazyLoadingMode: false,
      isVisibleAdvancedSearch: false,
      between_dates: null,
      student_sis_id: false,
      modal: {
        isVisible: {
          editApptPass: false
        }
      },
      ...tableConfig,
      activeTableRow: { item: { id: null }, index: null },
      isLoading: false,
      searchTimeOut: null,
      showTabs: false,
      pagination: {}
    })

    // ** Computed **
    const periods = computed(() => {
      return store.getters["periods/formListStudents"]
    })

    const editablePass = computed(() => {
      return store.getters["adultAptPass/editablePass"]
    })

    const passesTimeSettings = computed(() => {
      return store.getters["passes/passesTimeSettings"]
    })

    const currentUser = computed(() => {
      return store.getters["authentication/user"]
    })

    const filterData = computed(() => {
      return store.getters["dashboardTable/filterData"]
    })

    const aptFilterData = computed(() => {
      return store.getters["dashboardTable/aptFilterData"]
    })

    const dashboardPasses = computed(() => {
      return store.getters["dashboardTable/passes"]
    })

    const isActiveDashboardUpdate = computed(() => {
      return store.getters["dashboardTable/isActiveDashboardUpdate"]
    })

    const isActiveAppoinmentModule = computed(() => {
      return store.getters["schools/isActiveAppoinmentModule"]
    })

    const activeSchoolHasAppoitmentPasses = computed(() => {
      return store.getters["schools/activeSchoolHasAppoitmentPasses"]
    })

    const isUserInTransparency = computed(() => {
      return store.getters["authentication/isUserInTransparency"]
    })

    const stats = computed(() => {
      return store.getters["dashboardTable/stats"]
    })

    const activePassType = computed(() => {
      return store.getters["dashboardTable/getType"]
    })

    const activePassState = computed(() => {
      return store.getters["dashboardTable/getStatus"]
    })

    const school = computed(() => {
      return store.getters["schools/activeSchool"]
    })

    const areAppointmentPassesLoading = computed(
      () => store.getters["adultAptPass/areAppointmentPassesLoading"]
    )

    const isAppoinmentMode = computed(() => {
      return activePassType.value.includes("appointment")
    })

    const activeDataTableFields = computed(() => {
      return state.dataTableFields
        .filter((field) =>
          !state.isBasicView ? field.key !== "badge_flags" : true
        )
        .filter((field) =>
          !filterData.value.student_sis_id
            ? field.key !== "student_sis_id"
            : field
        )
    })

    const isStudentSisIdEnabled = computed(() => {
      return filterData.value.student_sis_id ? tableDropdowns.column[0] : false
    })

    const isAptStudentSisIdEnabled = computed(() => {
      return aptFilterData.value.student_sis_id
        ? tableDropdowns.columnApt[0]
        : false
    })

    // ** Statick data **
    const tableDropdowns = {
      column: [
        {
          value: "student_id",
          label: "Student ID"
        }
      ],
      columnApt: [
        {
          value: "student_id_apt",
          label: "Student ID"
        }
      ],
      tableView: [
        {
          value: false,
          label: "Pass view",
          icon: "ri-list-check-2"
        },
        {
          value: true,
          label: "Tabular view(CSV)",
          icon: "ri-align-justify"
        }
      ]
    }

    // ** Mounted **
    onMounted(() => {
      store.dispatch("rooms/getAutoPassLimit")
      if (activePassState.value == "ended") {
        store.dispatch("dashboardTable/getDashboardPassesData")
      } else {
        store
          .dispatch("dashboardTable/getDashboardPassesActiveData")
          .finally(() => {
            store.dispatch("dashboardTable/getDashboardPassesData")
          })
      }
      //Init the counter for APP passes in dashboard
      if (isActiveAppoinmentModule.value) {
        store.dispatch("dashboardCounters/getAppointmentsCounters")
      }

      store.dispatch("dashboardTable/getDashboard")
      if (!passesTimeSettings.value) {
        store.dispatch("passes/getPassesTimeSetting")
      }
    })

    // ** Methods **
    const onColumnOptionsChange = (value) => {
      if (isAppoinmentMode.value) {
        store.commit("dashboardTable/UPDATE_APT_FILTERS_DATA", {
          filter: "student_sis_id",
          value: value.includes("student_id_apt") ? true : false
        })
      } else {
        store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
          filter: "student_sis_id",
          value: value.includes("student_id") ? true : false
        })
      }
    }

    const exportAsCSV = () => {
      if (isAppoinmentMode.value) {
        state.isLoading = true
        store
          .dispatch("adultAptPass/getCSVAppointmentPasses", {
            student_sis_id: aptFilterData.value.student_sis_id
          })
          .then((response) => {
            if (response.data) {
              download.CSVExport(response.data, "appointment-passes")
            }
            state.isLoading = false
          })
          .catch(() => {
            state.isLoading = false
          })
      } else {
        state.isLoading = true
        store
          .dispatch("dashboardTable/getDashboardPassesExport")
          .then((response) => {
            if (response.data) {
              download.CSVExport(response.data, "passes")
            }
            state.isLoading = false
          })
          .catch(() => {
            state.isLoading = false
          })
      }
    }

    const clearFilter = () => {
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_students",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_teachers",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_rooms",
          value: []
        }
      ])
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", true)
      state.isLoading = true
      store.dispatch("dashboardTable/getDashboardPassesData").then(() => {
        state.isLoading = false
      })
    }

    const setPerPage = (option) => {
      if (option) {
        state.isLazyLoadingMode = isAllEntriesPerPageOption(option)
        state.pagination.per_page = option // problem !!!
      }
    }

    const setActivePage = () => {
      helpers.scrollToTop()
    }

    const setActiveRow = (value, index, column, event) => {
      const path = event.path || (event.composedPath && event.composedPath())
      if (state.isBasicView) {
        let isModal = false
        if (path) {
          path.forEach((element) => {
            const classes = element.className
            if (classes && classes == "no-target") {
              isModal = true
            }
          })
        }

        const targetClasses = event.target.classList
        if (!targetClasses.contains("no-target") && !isModal) {
          const dashboardTable = document.getElementById("dashboardTable")
          const rows = dashboardTable.getElementsByTagName("tr")
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i]
            row.classList.remove("active")
          }
          if (
            state.activeTableRow &&
            state.activeTableRow.item.id === value.id
          ) {
            state.activeTableRow = { item: { id: null }, index: null }
          } else {
            state.activeTableRow = { item: value, index }
            if (rows[index + 1]) {
              rows[index + 1].classList.add("active")
            }
          }
        }
      }
    }

    const clearActiveRow = () => {
      const dashboardTable = document.getElementById("dashboardTable")
      const rows = dashboardTable.getElementsByTagName("tr")
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        row.classList.remove("active")
      }
    }

    const sortByColumn = (column) => {
      const columnName = state.columnSearchKeys[column.column]
        ? state.columnSearchKeys[column.column].sortBy
        : column.column
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "sort",
          value: `${columnName}:${column.asc ? "asc" : "desc"}`
        }
      ])
    }

    const searchByQuery = (value) => {
      if (window.searchByQueryTimeout) {
        clearTimeout(window.searchByQueryTimeout)
      }

      window.searchByQueryTimeout = setTimeout(() => {
        if (isAppoinmentMode.value) {
          store.commit("adultAptPass/SET_SEARCH_QUERY", value)
          store.dispatch("adultAptPass/getAppointmentPasses")
        } else {
          store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
            filter: "search_query",
            value: value != "" ? value : null
          })
          store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
            {
              filter: "global_search",
              value: 1
            }
          ])
          state.isLoading = true
          store.dispatch("dashboardTable/getDashboardPassesData").then(() => {
            state.isLoading = false
          })
        }
        if (isActiveDashboardUpdate.value == true) {
          store.commit("dashboardTable/SET_DASHBOARD_UPDATES", false)
        }
      }, 1000)
    }

    const getPassStatus = (pass) => {
      if (pass.badge_flags) {
        if (pass.badge_flags.waiting_approval) {
          return {
            label: "Waiting Approval",
            class: "waiting-approval",
            color: "#141d24"
          }
        }
        if (pass.badge_flags.active && !pass.badge_flags.min_time) {
          return { label: "Active", class: "active", color: "#006d56" }
        }
        if (pass.badge_flags.ended) {
          return { label: "Ended", class: "ended" }
        }
        if (pass.badge_flags.system_completed) {
          return { label: "System Ended", class: "system-completed" }
        }
        if (pass.badge_flags.min_time) {
          return {
            label: passesTimeSettings.value
              ? helpers.formatDate(
                  passesTimeSettings.value.min_time,
                  "hh:mm:ss",
                  "m"
                ) + "+ MIN"
              : "Min time",
            class: "min-time",
            color: "#7a5600"
          }
        }
        if (pass.badge_flags.extended) {
          return { label: "Extended", class: "extended" }
        }
        if (pass.badge_flags.missed) {
          return { label: "Missed", class: "missed", color: "#007c71" }
        }
        if (pass.badge_flags.canceled) {
          return { label: "Canceled", class: "canceled", color: "#4a4fb3" }
        }
      }
      return ""
    }

    const advancedFilter = () => {
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", false)
      state.isLoading = true
      store.dispatch("dashboardTable/getDashboardPassesData").then(() => {
        state.isLoading = false
      })
    }

    const resumeDashboardUpdate = () => {
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", true)
      if (isAppoinmentMode.value) {
        store.dispatch("adultAptPass/getAppointmentPasses")
      } else {
        store.dispatch("dashboardTable/getDashboardPassesData")
      }
    }

    const setActiveView = (item) => {
      state.isBasicView = item.value
    }

    const toggleFilters = () => {
      state.isFilterVisible = !state.isFilterVisible
    }

    return {
      state,
      tableDropdowns,
      periods,
      editablePass,
      passesTimeSettings,
      currentUser,
      filterData,
      aptFilterData,
      dashboardPasses,
      isActiveDashboardUpdate,
      isActiveAppoinmentModule,
      activeSchoolHasAppoitmentPasses,
      isUserInTransparency,
      stats,
      activePassType,
      activePassState,
      school,
      areAppointmentPassesLoading,
      isAppoinmentMode,
      activeDataTableFields,
      isStudentSisIdEnabled,
      isAptStudentSisIdEnabled,
      onColumnOptionsChange,
      exportAsCSV,
      clearFilter,
      setPerPage,
      setActivePage,
      setActiveRow,
      clearActiveRow,
      sortByColumn,
      searchByQuery,
      getPassStatus,
      advancedFilter,
      resumeDashboardUpdate,
      setActiveView,
      toggleFilters
    }
  }
}
</script>
